import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Leaf, Heart, Sparkles, Gift, Mail, Lock } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

// Use Link for navigation
const TransitionLink = Link;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_beauty-bazaar-117/artifacts/2j1k3po7_Reggae%20Essences%20Logo.png";

export const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        await axios.post(`${API}/seed`);
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    // Simulate subscription - would connect to email service
    setTimeout(() => {
      toast.success('Welcome to the Reggae Essences family! Check your inbox for exclusive offers.');
      setEmail('');
      setSubscribing(false);
    }, 1000);
  };

  const featuredProducts = products.slice(0, 4);

  return (
    <div data-testid="home-page" className="min-h-screen bg-cream">
      {/* Admin Login Button - Fixed Position */}
      <Link 
        to="/admin/login" 
        className="fixed bottom-6 left-6 z-50 bg-forest/90 hover:bg-forest text-cream p-3 rounded-full shadow-lg transition-all hover:scale-105"
        data-testid="admin-login-btn"
        title="Admin Login"
      >
        <Lock className="h-5 w-5" />
      </Link>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background with warm earth tones */}
        <div className="absolute inset-0 bg-gradient-to-b from-forest to-forest-dark">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A962' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 text-center">
          <div className="relative inline-block mb-8">
            <img
              src={LOGO_URL}
              alt="Reggae Essences"
              className="h-52 md:h-72 w-auto mx-auto animate-fade-in hummingbird-logo"
            />
          </div>
          
          <p className="text-gold font-mono text-sm uppercase tracking-widest mb-4 animate-fade-in">
            Naturally Rooted. Soulfully Made.
          </p>
          
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-cream mb-6 animate-fade-in">
            Island Vibes for
            <br />
            <span className="text-gold">Skin & Soul</span>
          </h1>
          
          <p className="text-lg md:text-xl text-cream/80 max-w-2xl mx-auto mb-10 animate-fade-in">
            Handmade care with island rhythm. Experience luxurious self-care with our 
            small-batch soaps, whipped body butters, and soulful candles — crafted with 
            love and skin-loving ingredients.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in">
            <TransitionLink to="/shop">
              <Button
                data-testid="shop-now-btn"
                className="bg-gold hover:bg-gold/90 text-forest-dark px-8 py-6 text-lg rounded-full font-semibold"
              >
                Shop Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </TransitionLink>
            <TransitionLink to="/about">
              <Button
                variant="outline"
                data-testid="our-story-btn"
                className="border-2 border-cream/50 text-cream hover:bg-cream/10 px-8 py-6 text-lg rounded-full"
              >
                Our Story
              </Button>
            </TransitionLink>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-cream/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-cream/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Tagline Banner */}
      <section className="py-6 bg-gold/10 border-y border-gold/20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="font-heading text-lg md:text-xl text-warm-brown italic">
            "More than products — an experience and a vibe"
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-terracotta font-mono text-sm uppercase tracking-wider">
              Why Reggae Essences
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-forest mt-2">
              What Makes Us Different
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                icon: Leaf,
                title: 'All Natural',
                description: 'Skin-loving ingredients chosen for their nourishing benefits. No harsh chemicals.',
              },
              {
                icon: Heart,
                title: 'Small Batch Made',
                description: 'Handmade in small batches with love, intention, and island soul.',
              },
              {
                icon: Sparkles,
                title: 'Island Inspired',
                description: 'Earthy and island-inspired scents that transport you to paradise.',
              },
              {
                icon: Gift,
                title: 'Gift Ready',
                description: 'Beautiful self-care bundles perfect for treating yourself or gifting.',
              },
            ].map((feature, index) => (
              <div
                key={index}
                data-testid={`feature-${index}`}
                className="p-8 rounded-2xl bg-sand/50 border border-warm-brown/10 hover:border-gold/30 transition-colors text-center group"
              >
                <div className="w-16 h-16 bg-forest/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-gold/20 transition-colors">
                  <feature.icon className="h-8 w-8 text-forest" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-forest mb-3">
                  {feature.title}
                </h3>
                <p className="text-warm-brown">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 bg-sand/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-terracotta font-mono text-sm uppercase tracking-wider">
                Our Collection
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-forest mt-2">
                Featured Products
              </h2>
            </div>
            <TransitionLink
              to="/shop"
              className="hidden md:flex items-center gap-2 text-forest font-medium hover:text-gold transition-colors"
            >
              View All
              <ArrowRight className="h-4 w-4" />
            </TransitionLink>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-warm-brown/20 rounded-3xl mb-4" />
                  <div className="h-4 bg-warm-brown/20 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-warm-brown/20 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <TransitionLink
            to="/shop"
            className="md:hidden flex items-center justify-center gap-2 text-forest font-medium mt-8"
          >
            View All Products
            <ArrowRight className="h-4 w-4" />
          </TransitionLink>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 bg-forest">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-gold font-mono text-sm uppercase tracking-wider">
              Browse By
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-cream mt-2">
              Our Categories
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'Handmade Soaps',
                slug: 'soaps',
                price: '$6',
                description: 'Gently cleanse while nourishing your skin',
                image: 'https://customer-assets.emergentagent.com/job_beauty-bazaar-117/artifacts/vcv7ymch_Screenshot_20260206_130334_Facebook.jpg',
              },
              {
                name: 'Body Butters',
                slug: 'body_butters',
                price: '$12',
                description: 'Whipped to perfection for silky smooth skin',
                image: 'https://images.pexels.com/photos/4202465/pexels-photo-4202465.jpeg',
              },
              {
                name: 'Candles',
                slug: 'candles',
                price: '$12',
                description: 'Clean-burning with soulful island scents',
                image: 'https://images.pexels.com/photos/5775023/pexels-photo-5775023.jpeg',
              },
            ].map((category) => (
              <TransitionLink
                key={category.slug}
                to={`/shop?category=${category.slug}`}
                data-testid={`category-${category.slug}`}
                className="group relative overflow-hidden rounded-3xl aspect-[4/5]"
              >
                <img
                  src={category.image}
                  alt={category.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/90 via-forest-dark/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <span className="font-mono text-gold text-sm">From {category.price}</span>
                  <h3 className="font-heading text-2xl font-bold text-cream mt-1">
                    {category.name}
                  </h3>
                  <p className="text-cream/70 mt-2">{category.description}</p>
                </div>
              </TransitionLink>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Story Teaser */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-terracotta font-mono text-sm uppercase tracking-wider">
                Our Story
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-forest mt-2 mb-6">
                Naturally Rooted.<br />Soulfully Made.
              </h2>
              <div className="space-y-4 text-warm-brown">
                <p>
                  Reggae Essences was born from a love of natural self-care and the 
                  soulful rhythm of island living. Every product we create is a celebration 
                  of earthy ingredients, warm scents, and the simple joy of taking care of yourself.
                </p>
                <p>
                  We believe self-care should feel like a retreat — calm, grounded, and 
                  full of good vibes. That's why we handcraft each soap, whip every body 
                  butter, and pour every candle with intention and love.
                </p>
              </div>
              <TransitionLink to="/about">
                <Button
                  data-testid="learn-more-btn"
                  className="mt-8 bg-forest hover:bg-forest-light text-cream px-8 py-4 rounded-full"
                >
                  Learn More About Us
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </TransitionLink>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_beauty-bazaar-117/artifacts/dezjm8sx_Screenshot_20260206_130421_Facebook.jpg"
                alt="Our Products"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-gold text-forest-dark p-6 rounded-2xl shadow-xl">
                <div className="font-heading text-4xl font-bold">100%</div>
                <div className="text-sm font-medium">Natural Ingredients</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-forest-dark">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Mail className="h-12 w-12 text-gold mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-cream mb-4">
            Join the Reggae Essences Family
          </h2>
          <p className="text-cream/70 text-lg mb-8">
            Subscribe for early access to new launches, exclusive discounts, and self-care tips 
            delivered straight to your inbox.
          </p>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="flex-1 rounded-full bg-cream/10 border-cream/20 text-cream placeholder:text-cream/50 focus:border-gold"
              data-testid="newsletter-email"
            />
            <Button
              type="submit"
              disabled={subscribing}
              className="bg-gold hover:bg-gold/90 text-forest-dark rounded-full px-8"
              data-testid="newsletter-submit"
            >
              {subscribing ? 'Joining...' : 'Subscribe'}
            </Button>
          </form>
          <p className="text-cream/50 text-sm mt-4">
            We respect your privacy. Unsubscribe anytime.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-sand/50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-forest mb-6">
            Ready to Treat Yourself?
          </h2>
          <p className="text-warm-brown text-lg mb-8">
            Experience the calm, confident, and grounded energy of Reggae Essences. 
            Your skin (and soul) will thank you.
          </p>
          <TransitionLink to="/shop">
            <Button
              data-testid="start-shopping-btn"
              className="bg-terracotta hover:bg-terracotta/90 text-cream px-10 py-6 text-lg rounded-full"
            >
              Start Shopping
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </TransitionLink>
        </div>
      </section>
    </div>
  );
};
